import { useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

/**
 * Manages query parameters related to pagination, synchronizing state with URL parameters.
 *
 * @param {Object} param0 - An object containing parameters.
 * @param {number} param0.count - Total number of items.
 * @param {boolean} param0.isFetching - Flag indicating if data is being fetched.
 * @param {boolean} [param0.queryParams=true] - Whether to use query parameters for pagination.
 * @param {number} [param0.initialPageSize=25] - Initial page size.
 * @returns {Object} An object containing page, pageSize, updatePage, and updatePageSize.
 */
export default function useQueryParams({
  count,
  isFetching,
  page,
  setPage,
  pageSize,
  setPageSize,
  queryParams = true,
}: {
  count: number;
  isFetching: boolean;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  queryParams?: boolean;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Calculate total pages and last valid page
  const totalPages = Math.ceil(count / pageSize);
  const lastValidPage = Math.max(totalPages - 1, 0);

  // Sync URL with page and pageSize if they change
  useEffect(() => {
    if (queryParams) {
      const urlPageSize = searchParams.get("pageSize");
      if (urlPageSize && parseInt(urlPageSize) !== pageSize) {
        const newParams: { [key: string]: string } = {
          page: (page + 1).toString(),
          pageSize: pageSize.toString(),
        };
        const currentSearch = searchParams.get("search");
        if (currentSearch) {
          newParams.search = currentSearch;
        }
        setSearchParams(newParams);
      }
    }
  }, [searchParams, pageSize, page, setSearchParams, queryParams]);

  // Validate page and redirect if invalid
  useEffect(() => {
    if (queryParams && !isFetching) {
      if (isNaN(page) || isNaN(pageSize) || page < 0 || pageSize < 1) {
        navigate("/notFound", { replace: true });
      }
    }
  }, [page, pageSize, queryParams, navigate, isFetching]);

  // Adjust page if it exceeds last valid page
  useEffect(() => {
    if (queryParams) {
      if (!isFetching && count > 0 && page > lastValidPage) {
        const newPage = lastValidPage;
        const newParams: { [key: string]: string } = {
          page: (newPage + 1).toString(),
          pageSize: pageSize.toString(),
        };
        const currentSearch = searchParams.get("search");
        if (currentSearch) {
          newParams.search = currentSearch;
        }
        setSearchParams(newParams);
        setPage(newPage);
      }
    }
  }, [
    isFetching,
    count,
    page,
    lastValidPage,
    pageSize,
    setSearchParams,
    queryParams,
    setPage,
    searchParams,
  ]);

  // Custom setPage function to handle page changes
  const updatePage = useCallback(
    (newPage: number) => {
      if (queryParams) {
        const validPage = Math.min(Math.max(newPage, 0), lastValidPage);
        const newParams: { [key: string]: string } = {
          page: (validPage + 1).toString(),
          pageSize: pageSize.toString(),
        };
        const currentSearch = searchParams.get("search");
        if (currentSearch) {
          newParams.search = currentSearch;
        }
        setSearchParams(newParams);
        setPage(validPage);
      } else {
        setPage(newPage);
      }
    },
    [
      queryParams,
      lastValidPage,
      setSearchParams,
      pageSize,
      setPage,
      searchParams,
    ],
  );

  // Custom setPageSize function to handle page size changes
  const updatePageSize = useCallback(
    (newPageSize: number) => {
      const newTotalPages = Math.ceil(count / newPageSize) || 1;
      const newLastValidPage = Math.max(newTotalPages - 1, 0);
      const newPage = Math.min(page, newLastValidPage);
      if (queryParams) {
        const newParams: { [key: string]: string } = {
          page: (newPage + 1).toString(),
          pageSize: newPageSize.toString(),
        };
        const currentSearch = searchParams.get("search");
        if (currentSearch) {
          newParams.search = currentSearch;
        }
        setSearchParams(newParams);
      }
      setPageSize(newPageSize);
      setPage(newPage);
    },
    [
      count,
      page,
      queryParams,
      setPageSize,
      setPage,
      setSearchParams,
      searchParams,
    ],
  );

  return {
    page,
    pageSize,
    updatePage,
    updatePageSize,
  };
}
